/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { Row, Col, Card, CardBody, Table, Button, Badge } from "reactstrap";
import Select from "react-select";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const VehicleExpenseReport = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState(null);
    const [vehicles, setVehicles] = useState([]);

    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [activeShortcut, setActiveShortcut] = useState('all');

    const handleShortcut = (shortcut) => {
        setActiveShortcut(shortcut);
        let from = null;
        let to = dayjs();
        
        if (shortcut === 'today') {
            from = dayjs();
            to = dayjs();
        } else if (shortcut === 'week') {
            from = dayjs().startOf('week');
        } else if (shortcut === 'month') {
            from = dayjs().startOf('month');
        } else if (shortcut === 'year') {
            from = dayjs().startOf('year');
        } else if (shortcut === 'all') {
            from = null;
            to = null;
        }
        
        setFromDate(from);
        setToDate(to);
    };

    const loadReport = async () => {
        dispatch(loadingTrue());
        let url = `/reports/expenses.php?1=1`; 
        if (selectedVehicle) url += `&vehicle_id=${selectedVehicle.value}`;
        if (fromDate) url += `&from_date=${fromDate.format("YYYY-MM-DD")}`;
        if (toDate) url += `&to_date=${toDate.format("YYYY-MM-DD")}`;

        const res = await Handler({ method: "get", url });
        if (res?.status || res?.success) {
            setReportData(res.data);
        }
        dispatch(loadingFalse());
    };

    const loadMasterData = async () => {
        const res = await Handler({ method: "get", url: "/vehicle/list.php" });
        if (res?.status || res?.success) {
            setVehicles(res.data.map(v => ({ value: v.id, label: `${v.vehicle_number}` })));
        }
    };

    useEffect(() => {
        loadMasterData();
    }, []);

    useEffect(() => {
        loadReport();
    }, [fromDate, toDate, selectedVehicle]);

    if (!reportData) return null;

    const { summary, expense_type_chart, expense_trend_chart, vehicle_expense_chart, list } = reportData;

    // ⭐ Fix: Ensure values are strictly numbers for Recharts
    const pieDataFormatted = expense_type_chart?.map(item => ({
        name: item.expense_type,
        value: parseFloat(item.total || 0)
    })) || [];

    const barDataFormatted = vehicle_expense_chart?.map(item => ({
        vehicle: item.vehicle_id,
        amount: parseFloat(item.total || 0)
    })) || [];

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

    return (
        <div className="report-shell p-4 min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
            
            {/* Header Area */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h2 className="fw-800 text-slate-900 mb-0">Vehicle Expense Hub</h2>
                    <p className="text-slate-500 fw-500">Financial intelligence for your fleet operations</p>
                </div>
                
                {/* Floating Filter Bar */}
                <div className="filter-bar shadow-sm d-flex align-items-center gap-2 p-2 bg-white rounded-pill border flex-wrap">
                    <div className="btn-group me-2 ms-2" role="group">
                        {['all', 'today', 'week', 'month', 'year'].map((shortcut) => (
                            <Button
                                key={shortcut}
                                color={activeShortcut === shortcut ? "indigo" : "light"}
                                size="sm"
                                className="rounded-pill px-3 py-1 me-1 border-0"
                                style={{ 
                                    textTransform: 'capitalize', 
                                    fontSize: '11px', 
                                    fontWeight: 'bold',
                                    backgroundColor: activeShortcut === shortcut ? '#6366f1' : '#f1f5f9',
                                    color: activeShortcut === shortcut ? '#fff' : '#64748b'
                                }}
                                onClick={() => handleShortcut(shortcut)}
                            >
                                {shortcut}
                            </Button>
                        ))}
                    </div>
                    <div className="vr mx-2" style={{ height: 20 }}></div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div style={{ width: 150 }}>
                            <Select 
                                options={vehicles} 
                                value={selectedVehicle} 
                                onChange={setSelectedVehicle} 
                                placeholder="Select Vehicle"
                                isClearable
                                styles={{ control: (b) => ({ ...b, border: 0, boxShadow: 'none', background: 'transparent' }) }}
                            />
                        </div>
                        <div className="vr mx-2" style={{ height: 20 }}></div>
                        <DatePicker 
                            value={fromDate} 
                            onChange={(val) => {
                                setFromDate(val);
                                setActiveShortcut('');
                            }} 
                            slotProps={{ textField: { variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 100 } } }} 
                        />
                        <span className="text-slate-300 mx-1">/</span>
                        <DatePicker 
                            value={toDate} 
                            onChange={(val) => {
                                setToDate(val);
                                setActiveShortcut('');
                            }} 
                            slotProps={{ textField: { variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 100 } } }} 
                        />
                        <Button color="indigo" className="rounded-pill px-4 fw-bold text-white shadow-sm" style={{ backgroundColor: '#6366f1', border: 0 }} onClick={loadReport}>
                            Refresh
                        </Button>
                    </LocalizationProvider>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <Row className="mb-4 g-4">
                <Col lg={4}>
                    <div className="stat-card p-4 rounded-4 bg-white border-0 shadow-sm d-flex align-items-center justify-content-between h-100">
                        <div>
                            <span className="text-slate-400 small uppercase fw-bold ls-1">Expenditure</span>
                            <h2 className="fw-800 text-slate-900 mb-0 mt-1">SGD {parseFloat(summary?.total_amount || 0).toLocaleString()}</h2>
                        </div>
                        <div className="icon-box bg-indigo-50 text-indigo-600 rounded-circle p-3">
                            <i className="bi bi-wallet-fill h3 mb-0"></i>
                        </div>
                    </div>
                </Col>
                <Col lg={4}>
                    <div className="stat-card p-4 rounded-4 bg-white border-0 shadow-sm d-flex align-items-center justify-content-between h-100">
                        <div>
                            <span className="text-slate-400 small uppercase fw-bold ls-1">Record Count</span>
                            <h2 className="fw-800 text-slate-900 mb-0 mt-1">{summary?.total_expenses}</h2>
                        </div>
                        <div className="icon-box bg-emerald-50 text-emerald-600 rounded-circle p-3">
                            <i className="bi bi-clipboard2-data-fill h3 mb-0"></i>
                        </div>
                    </div>
                </Col>
                <Col lg={4}>
                    <div className="stat-card p-4 rounded-4 bg-indigo-600  border-0 shadow-lg d-flex align-items-center justify-content-between h-100">
                        <div>
                            <span className=" small uppercase fw-bold ls-1">Avg Cost / Log</span>
                            <h2 className="fw-800 mb-0 mt-1">${summary?.total_expenses > 0 ? (summary?.total_amount / summary?.total_expenses).toFixed(2) : '0.00'}</h2>
                        </div>
                        <div className="icon-box bg-opacity-20 bg-dark rounded-circle p-3 text-white">
                            <i className="bi bi-graph-up-arrow h3 mb-0"></i>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Main Graphics Section */}
            <Row className="mb-4 g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <CardBody className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h6 className="fw-bold text-slate-800 mb-0">Cost Trajectory</h6>
                                <Badge color="indigo-soft" className="text-indigo-600">Daily Updates</Badge>
                            </div>
                            <div style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={expense_trend_chart}>
                                        <defs>
                                            <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="expense_date" axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} tickFormatter={s => dayjs(s).format('DD MMM')} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} />
                                        <Tooltip contentStyle={{ borderRadius: '12px', border: 0, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                        <Area type="monotone" dataKey="total" stroke="#6366f1" strokeWidth={4} fill="url(#colorArea)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <CardBody className="p-4 d-flex flex-column">
                            <h6 className="fw-bold text-slate-800 mb-4 text-center">Cost Allocation</h6>
                            <div style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={pieDataFormatted}
                                            innerRadius={70}
                                            outerRadius={100}
                                            paddingAngle={8}
                                            dataKey="value"
                                            nameKey="name"
                                        >
                                            {pieDataFormatted.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={4} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend align="center" iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Assets Comparison Grid */}
            <Row className="g-4 mb-4">
                <Col lg={12}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <CardBody className="p-4">
                            <h6 className="fw-bold text-slate-800 mb-4">Asset (Vehicle) Expenditure Comparison</h6>
                            <div style={{ width: '100%', height: 280 }}>
                                <ResponsiveContainer>
                                    <BarChart data={barDataFormatted}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="vehicle" axisLine={false} tickLine={false} fontSize={12} tick={{ fill: '#64748b', fontWeight: 600 }} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} />
                                        <Tooltip cursor={{ fill: '#f8fafc' }} />
                                        <Bar dataKey="amount" fill="#cbd5e1" radius={[8, 8, 0, 0]} barSize={45}>
                                            {barDataFormatted.map((entry, index) => (
                                                <Cell key={`c-${index}`} fill={index === 0 ? '#6366f1' : '#cbd5e1'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Master Transaction Ledger */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="bg-white p-4 border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-slate-900">Transaction Ledger</h6>
                    {/* <div className="d-flex gap-2">
                        <Button color="light" size="sm" className="border rounded-pill px-3 fw-bold small text-slate-600">Export PDF</Button>
                    </div> */}
                </div>
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                        <thead className="bg-slate-50 text-slate-400 small uppercase">
                            <tr>
                                <th className="ps-4 py-3">Date</th>
                                <th>Vehicle</th>
                                {/* <th>Category</th> */}
                                <th>Details</th>
                                <th className="text-end pe-4">Sum (SGD)</th>
                            </tr>
                        </thead>
                        <tbody className="small">
                            {list?.map((row, idx) => (
                                <tr key={idx}>
                                    <td className="ps-4 fw-600 text-slate-700">{dayjs(row.expense_date).format("MMM DD, YYYY")}</td>
                                    <td><span className="badge-custom">{row.vehicle_id}</span></td>
                                    {/* <td><span className="text-indigo-600 fw-bold">{row.expense_type}</span></td> */}
                                    <td className="text-slate-500 italic" style={{ maxWidth: 300 }}>{row.remarks || '---'}</td>
                                    <td className="text-end pe-4 fw-800 text-slate-900">${parseFloat(row.amount).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .report-shell { font-family: 'Plus Jakarta Sans', sans-serif; }
                .fw-800 { font-weight: 800; }
                .fw-600 { font-weight: 600; }
                .ls-1 { letter-spacing: 0.05em; }
                .bg-indigo-50 { background-color: #061f73; }
                .text-indigo-600 { color: #fff; }
                .bg-emerald-50 { background-color: #075933; }
                .text-emerald-600 { color: #fff; }
                .badge-custom { background: #cad6e2; color: #000; padding: 4px 10px; border-radius: 8px; font-weight: 700; font-size: 11px; }
                .bg-slate-50 { background-color: #0e3761; }
                .text-slate-400 { color: #94a3b8; }
                .text-slate-900 { color: #0f172a; }
                .indigo-soft { background-color: #eef2ff; border: 1px solid #e0e7ff; }
                .table thead th { border-top: 0; }
                .table tbody tr:hover { background-color: #ddd !important; }
            `}</style>
        </div>
    );
};

export default VehicleExpenseReport;